import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Lightbulb, PenTool, Code, Bug, Rocket, Headphones } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

gsap.registerPlugin(ScrollTrigger);

const ICONS = [Lightbulb, PenTool, Code, Bug, Rocket, Headphones];

const Process = () => {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add("(min-width: 1024px)", () => {
        const track = trackRef.current;
        if (!track) return;
        const scrollLength = track.scrollWidth - window.innerWidth + 120;
        gsap.to(track, {
          x: -scrollLength,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: () => `+=${scrollLength}`,
            scrub: 0.6,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, [t]);

  return (
    <section
      id="process"
      ref={sectionRef}
      data-testid="process-section"
      className="relative py-24 md:py-32 lg:py-0 lg:min-h-screen overflow-hidden"
    >
      <div className="radial-blob" style={{ width: 600, height: 600, background: "#0055FF", top: "20%", right: "-20%", opacity: 0.2 }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:pt-24">
        <div className="max-w-3xl">
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#00F0FF] mb-4">
            {t.process.overline}
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight gradient-text" data-testid="process-title">
            {t.process.title}
          </h2>
          <p className="mt-5 text-[#9BA1B0] text-base sm:text-lg font-body">{t.process.subtitle}</p>
        </div>
      </div>

      <div className="mt-14 lg:mt-20 overflow-hidden">
        <div
          ref={trackRef}
          className="flex flex-col lg:flex-row items-stretch gap-6 lg:gap-0 px-6 md:px-12 lg:w-max"
        >
          {t.process.steps.map((step, i) => {
            const Icon = ICONS[i] || Lightbulb;
            const isLast = i === t.process.steps.length - 1;
            return (
              <div key={i} className="flex items-stretch lg:items-center" data-testid={`process-step-${i}`}>
                <div className="glass rounded-2xl p-7 md:p-8 lg:w-[360px] xl:w-[400px]">
                  <div className="flex items-center gap-4">
                    <div className="process-node">
                      <Icon size={24} className="text-[#00F0FF]" strokeWidth={1.6} />
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#00F0FF]">
                        Step 0{i + 1}
                      </div>
                      <h3 className="font-heading font-bold text-2xl text-white mt-1">{step.name}</h3>
                    </div>
                  </div>
                  <p className="mt-5 text-sm md:text-base text-[#9BA1B0] font-body leading-relaxed">
                    {step.desc}
                  </p>
                </div>
                {!isLast && (
                  <div className="hidden lg:flex items-center w-24 px-6" aria-hidden="true">
                    <div className="w-full h-px bg-gradient-to-r from-[#00F0FF]/60 via-[#00F0FF]/30 to-[#0055FF]/60 relative">
                      <span className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#00F0FF] shadow-[0_0_12px_rgba(0,240,255,0.8)]" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Process;
